import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getRestaurantBookings, saveRestaurantBooking } from "../../components/Api/GmsRestaurantBooking";
import { getBranches } from "../../components/Api/GmsBranch";
import { searchCustomers } from "../../components/Api/GmsCustomer";
import { getRestaurantTables } from "../../components/Api/GmsRestaurantTable";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };
  
  let businessId, branchId;
  
  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }
  
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const defaultForm = {
  BookingID: 0,
  TableID: null,
  Customer_Id: null,
  NoOfGuests: 1,
  Booking_Date: new Date(),
  From_Time: new Date(),
  To_Time: new Date(),
  Status: 'Pending',
};

const statusOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Completed', label: 'Completed' },
];

const RestaurantBookingListPage = () => {
  const [bookings, setBookings] = useState([]);
  const [branches, setBranches] = useState([]);
  const [restaurantTables, setRestaurantTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Filter state
  const [filterCustomer, setFilterCustomer] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  
  const session = useMemo(() => getUserSession(), []);
  
  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsRestaurantBookingList');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsRestaurantBookingList');
  const isAdmin = isUserAdmin();

  useEffect(() => {
    if (hasPermission) {
      loadBranches();
      loadRestaurantTables();
      loadBookings();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && !selectedBranch) {
      // Find branch by matching value (since branches are already formatted as options with value/label)
      const branch = branches.find(b => b.value === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId]);

  // Load customers when branch changes
  useEffect(() => {
    if (selectedBranch && form.Customer_Id) {
      searchCustomersManually('');
    }
  }, [selectedBranch]);

  useEffect(() => {
    loadBookings();
  }, [page, filterCustomer, filterDate, filterStatus]);

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      const options = data.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranches(options);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadRestaurantTables = async () => {
    try {
      const data = await getRestaurantTables({ Active: true });
      const options = (data || []).map(t => ({
        value: t.tableID ?? t.TableID,
        label: `${t.tableNo ?? t.TableNo} — ${t.tableNameEn ?? t.TableNameEn}`
      }));
      setRestaurantTables(options);
    } catch (error) {
      console.error("Error loading restaurant tables:", error);
      setRestaurantTables([]);
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      const filter = {
        Customer_Id: filterCustomer?.value || null,
        Booking_Date: filterDate ? filterDate.toISOString().split('T')[0] : null,
        Status: filterStatus?.value || null,
        Page: page,
        PageSize: pageSize,
      };
      
      const response = await getRestaurantBookings(filter);
      setBookings(response.bookings || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error loading bookings:", error);
      Swal.fire("Error", "Failed to load restaurant bookings", "error");
    } finally {
      setLoading(false);
    }
  };

  const searchCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(inputValue, 1, 20);
      
      // Filter by branch if selected
      // Show customers that match the selected branch OR have null/undefined branch (global customers)
      let filteredCustomers = response.items || [];
      if (selectedBranch) {
        filteredCustomers = filteredCustomers.filter(c => {
          const branchId = c.customer_Branch_Id;
          const matchesBranch = branchId === selectedBranch.value;
          const isGlobalCustomer = branchId === null || branchId === undefined;
          return matchesBranch || isGlobalCustomer;
        });
      }
      
      const formattedOptions = filteredCustomers.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      console.log('Customer search results:', {
        inputValue,
        totalResults: response.items?.length || 0,
        filteredResults: filteredCustomers.length,
        formattedOptions: formattedOptions.length,
        selectedBranch: selectedBranch?.value,
        sampleCustomer: filteredCustomers[0] ? {
          id: filteredCustomers[0].customer_Id,
          name: filteredCustomers[0].customer_NameEn,
          branchId: filteredCustomers[0].customer_Branch_Id
        } : null
      });
      
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleEdit = (booking) => {
    // Convert TimeSpan strings to Date objects for time pickers
    const fromTime = new Date();
    const timeStr = booking.from_Time || booking.From_Time || '00:00:00';
    const [fromHours, fromMinutes] = timeStr.split(':').map(Number);
    fromTime.setHours(fromHours, fromMinutes, 0, 0);
    
    const toTime = new Date();
    const toTimeStr = booking.to_Time || booking.To_Time || '00:00:00';
    const [toHours, toMinutes] = toTimeStr.split(':').map(Number);
    toTime.setHours(toHours, toMinutes, 0, 0);
    
    setForm({
      BookingID: booking.bookingID || booking.BookingID,
      TableID: booking.tableID || booking.TableID,
      Customer_Id: booking.customer_Id || booking.Customer_Id,
      NoOfGuests: booking.noOfGuests || booking.NoOfGuests,
      Booking_Date: new Date(booking.booking_Date || booking.Booking_Date),
      From_Time: fromTime,
      To_Time: toTime,
      Status: booking.status || booking.Status,
    });
    
    // Set customer option
    const customerName = booking.customer_NameEn || booking.Customer_NameEn;
    const customerContact = booking.customer_ContactNo || booking.Customer_ContactNo;
    if (customerName) {
      setSelectedCustomerOption({
        value: booking.customer_Id || booking.Customer_Id,
        label: `${customerName} — ${customerContact || ''}`,
      });
    }
    
    setIsEditing(true);
    setShowModal(true);
  };

  const handleAdd = () => {
    setForm(defaultForm);
    setSelectedCustomerOption(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setForm(defaultForm);
    setSelectedCustomerOption(null);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.TableID) {
      Swal.fire("Error", "Please select a table", "error");
      return;
    }
    if (!form.Customer_Id) {
      Swal.fire("Error", "Please select a customer", "error");
      return;
    }
    
    if (!form.NoOfGuests || form.NoOfGuests < 1) {
      Swal.fire("Error", "Number of guests must be at least 1", "error");
      return;
    }
    
    if (form.To_Time <= form.From_Time) {
      Swal.fire("Error", "To Time must be after From Time", "error");
      return;
    }
    
    try {
      setSaving(true);
      
      // Convert Date objects to proper format
      const bookingData = {
        BookingID: form.BookingID,
        TableID: form.TableID,
        Customer_Id: form.Customer_Id,
        NoOfGuests: form.NoOfGuests || 1,
        Booking_Date: form.Booking_Date.toISOString().split('T')[0],
        From_Time: form.From_Time.toTimeString().substring(0, 8), // HH:mm:ss
        To_Time: form.To_Time.toTimeString().substring(0, 8), // HH:mm:ss
        Status: form.Status,
      };
      
      const response = await saveRestaurantBooking(bookingData);
      
      if (response.isSuccess) {
        Swal.fire("Success", response.message || "Restaurant booking saved successfully", "success");
        handleCloseModal();
        loadBookings();
      } else {
        Swal.fire("Error", response.message || "Failed to save restaurant booking", "error");
      }
    } catch (error) {
      console.error("Error saving booking:", error);
      Swal.fire("Error", error.response?.data?.message || "Failed to save restaurant booking", "error");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pending':
        return <span className="badge bg-warning">Pending</span>;
      case 'Cancelled':
        return <span className="badge bg-danger">Cancelled</span>;
      case 'Completed':
        return <span className="badge bg-success">Completed</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Restaurant Booking List</h4>
                  {hasEditOrHigher && (
                    <Button color="primary" onClick={handleAdd}>
                      Add Booking
                    </Button>
                  )}
                </div>

                {/* Filters */}
                <Card className="mb-4">
                  <CardBody>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Branch</Label>
                          <Select
                            value={selectedBranch}
                            onChange={(option) => {
                              setSelectedBranch(option);
                              setFilterCustomer(null);
                              setSelectedCustomerOption(null);
                            }}
                            options={branches}
                            isClearable={!session.branchId}
                            isDisabled={!!session.branchId}
                            placeholder="Select Branch"
                            styles={selectStyles}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Customer</Label>
                          <Select
                            value={filterCustomer}
                            onChange={(option) => {
                              setFilterCustomer(option);
                              setPage(1);
                            }}
                            onInputChange={(inputValue) => {
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }
                              const timeout = setTimeout(() => {
                                searchCustomersManually(inputValue);
                              }, 300);
                              setSearchTimeout(timeout);
                              return inputValue;
                            }}
                            options={customerSearchOptions}
                            isLoading={customerSearchLoading}
                            placeholder="Search customer (min 3 chars)"
                            styles={selectStyles}
                            isClearable
                            isSearchable
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Date</Label>
                          <DatePicker
                            selected={filterDate}
                            onChange={(date) => {
                              setFilterDate(date);
                              setPage(1);
                            }}
                            dateFormat="yyyy-MM-dd"
                            className="form-control"
                            isClearable
                            placeholderText="Select Date"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Status</Label>
                          <Select
                            value={filterStatus}
                            onChange={(option) => {
                              setFilterStatus(option);
                              setPage(1);
                            }}
                            options={statusOptions}
                            placeholder="Select Status"
                            styles={selectStyles}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Table */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Bookings ({totalCount} records)
                    </h5>
                    {loading ? (
                      <div className="text-center py-5">
                        <ClipLoader size={50} color="#556ee6" />
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table bordered>
                            <thead className="table-light">
                              <tr>
                                <th>Sr.No</th>
                                <th>Table</th>
                                <th>Customer</th>
                                <th>No. of Guests</th>
                                <th>Date</th>
                                <th>From Time</th>
                                <th>To Time</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bookings.length === 0 ? (
                                <tr>
                                  <td colSpan="9" className="text-center">
                                    No bookings found
                                  </td>
                                </tr>
                              ) : (
                                bookings.map((booking, index) => (
                                  <tr key={booking.bookingID || booking.BookingID}>
                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                    <td>{booking.tableNo || booking.TableNo || booking.tableNameEn || booking.TableNameEn || 'N/A'}</td>
                                    <td>{booking.customer_NameEn || booking.Customer_NameEn || 'N/A'}</td>
                                    <td>{booking.noOfGuests || booking.NoOfGuests}</td>
                                    <td>{(() => {
                                      const date = new Date(booking.booking_Date || booking.Booking_Date);
                                      const day = String(date.getDate()).padStart(2, '0');
                                      const month = String(date.getMonth() + 1).padStart(2, '0');
                                      const year = date.getFullYear();
                                      return `${day}/${month}/${year}`;
                                    })()}</td>
                                    <td>{(() => {
                                      const timeStr = booking.from_Time || booking.From_Time || '';
                                      // Remove seconds if present (HH:mm:ss -> HH:mm)
                                      return timeStr.split(':').slice(0, 2).join(':');
                                    })()}</td>
                                    <td>{(() => {
                                      const timeStr = booking.to_Time || booking.To_Time || '';
                                      // Remove seconds if present (HH:mm:ss -> HH:mm)
                                      return timeStr.split(':').slice(0, 2).join(':');
                                    })()}</td>
                                    <td>{getStatusBadge(booking.status || booking.Status)}</td>
                                    <td>
                                      {hasEditOrHigher && (
                                        <Button
                                          color="info"
                                          size="sm"
                                          onClick={() => handleEdit(booking)}
                                        >
                                          <SquarePen size={16} className="me-1" />
                                          Edit
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {/* Pagination */}
                        {totalCount > pageSize && (
                          <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                               style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                            <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                              Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                            </div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <Button
                                color="light"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                }}
                              >
                                <i className="mdi mdi-chevron-left"></i>
                              </Button>
                              
                              {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                const pageNum = index + 1;
                                const isActive = page === pageNum;
                                
                                return (
                                  <Button
                                    key={pageNum}
                                    color={isActive ? "primary" : "light"}
                                    size="sm"
                                    onClick={() => setPage(pageNum)}
                                    style={{
                                      border: '1px solid #dee2e6',
                                      borderRadius: '0.375rem',
                                      minWidth: '32px',
                                      height: '32px',
                                      backgroundColor: isActive ? '#556ee6' : '#fff',
                                      borderColor: isActive ? '#556ee6' : '#dee2e6',
                                      color: isActive ? '#fff' : '#495057',
                                    }}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                              
                              <Button
                                color="light"
                                size="sm"
                                disabled={page === Math.ceil(totalCount / pageSize)}
                                onClick={() => setPage(page + 1)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                }}
                              >
                                <i className="mdi mdi-chevron-right"></i>
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Add/Edit Modal */}
        <Modal isOpen={showModal} toggle={handleCloseModal} size="lg">
          <ModalHeader toggle={handleCloseModal}>
            {isEditing ? 'Edit Restaurant Booking' : 'Add Restaurant Booking'}
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>Branch *</Label>
                    <Select
                      value={selectedBranch}
                      onChange={(option) => {
                        setSelectedBranch(option);
                        setSelectedCustomerOption(null);
                        setForm({ ...form, Customer_Id: null });
                      }}
                      options={branches}
                      isClearable={!session.branchId}
                      isDisabled={!!session.branchId}
                      placeholder="Select Branch"
                      styles={selectStyles}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>Table *</Label>
                    <Select
                      value={restaurantTables.find(t => t.value === form.TableID) || null}
                      onChange={(option) => setForm({ ...form, TableID: option?.value || null })}
                      options={restaurantTables}
                      placeholder="Select Table"
                      styles={selectStyles}
                      isClearable
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>Customer *</Label>
                      <Select
                        value={selectedCustomerOption}
                        onChange={(option) => {
                          setSelectedCustomerOption(option);
                          setForm({ ...form, Customer_Id: option?.value || null });
                        }}
                        onInputChange={(inputValue) => {
                          if (searchTimeout) {
                            clearTimeout(searchTimeout);
                          }
                          const timeout = setTimeout(() => {
                            searchCustomersManually(inputValue);
                          }, 300);
                          setSearchTimeout(timeout);
                          return inputValue;
                        }}
                        options={customerSearchOptions}
                        isLoading={customerSearchLoading}
                        placeholder="Search customer (min 3 chars)"
                        styles={selectStyles}
                        isClearable
                        isSearchable
                        isDisabled={!selectedBranch}
                      />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>Date *</Label>
                    <DatePicker
                      selected={form.Booking_Date}
                      onChange={(date) => setForm({ ...form, Booking_Date: date })}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Select Date"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>No. of Guests *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.NoOfGuests || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string while typing, but convert to number for storage
                        if (value === '' || value === null || value === undefined) {
                          setForm({ ...form, NoOfGuests: '' });
                        } else {
                          const numValue = parseInt(value, 10);
                          if (!isNaN(numValue) && numValue > 0) {
                            setForm({ ...form, NoOfGuests: numValue });
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure minimum value of 1 when field loses focus
                        if (!form.NoOfGuests || form.NoOfGuests < 1) {
                          setForm({ ...form, NoOfGuests: 1 });
                        }
                      }}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>From Time *</Label>
                    <DatePicker
                      selected={form.From_Time}
                      onChange={(date) => setForm({ ...form, From_Time: date })}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="form-control"
                      placeholderText="Select From Time"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup style={{ marginBottom: '20px' }}>
                    <Label style={{ marginBottom: '8px', display: 'block' }}>To Time *</Label>
                    <DatePicker
                      selected={form.To_Time}
                      onChange={(date) => setForm({ ...form, To_Time: date })}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="form-control"
                      placeholderText="Select To Time"
                    />
                  </FormGroup>
                </Col>
                {isEditing && (
                  <Col md={6}>
                    <FormGroup style={{ marginBottom: '20px' }}>
                      <Label style={{ marginBottom: '8px', display: 'block' }}>Status *</Label>
                      <Select
                        value={statusOptions.find(opt => opt.value === form.Status)}
                        onChange={(option) => setForm({ ...form, Status: option?.value || 'Pending' })}
                        options={statusOptions}
                        styles={selectStyles}
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={handleCloseModal} disabled={saving}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? <ClipLoader size={20} color="#fff" /> : (isEditing ? 'Update' : 'Save')}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default RestaurantBookingListPage;
